import { LocalStorageHelper } from "@/utils/LocalStorageHelper";

/** Legacy global dismissal key; per-campaign keys append `:<entryId>`. */
export const PROMO_DISMISSED_KEY = "emailPromoModalDismissedAt";

/** Legacy global session key; per-campaign keys append `:<entryId>`. */
export const PROMO_SHOWN_SESSION_KEY = "emailPromoModalShownSession";

/** Suppresses every campaign for the session. Seeded by e2e/helpers/email-promo.ts. */
export const PROMO_DISABLED_SESSION_KEY = "emailPromoModalDisabled";

/**
 * Caps "Popup Suppressed" at one event per session. The modal is in the site layout and its
 * trigger arms on every page load, so an unguarded event would fire on every page view for the
 * 30 days a dismissal lasts — inflating the suppression rate and the request count alike.
 */
export const PROMO_SUPPRESSION_REPORTED_SESSION_KEY = "emailPromoModalSuppressionReported";

const SUPPRESS_HOURS = 30 * 24;

/** Reported as `suppressionReason` on "Popup Suppressed" event. */
export type PromoSuppressionReason = "session_shown" | "recently_dismissed";

interface Campaign {
  id: string;
  /** Global (untargeted) campaigns also honor the pre-EN-5837 global keys. */
  isGlobal: boolean;
}

function isDismissed(key: string): boolean {
  return !LocalStorageHelper.isCacheExpired(key, SUPPRESS_HOURS);
}

/**
 * Why a campaign's modal should stay closed, or `null` when it may show.
 *
 * `ignoreDismissal` is the preview escape hatch, but the session guard still applies — the
 * live-preview iframe reloads on every content edit and would re-pop the modal each time.
 *
 * Storage access can throw (Safari private mode, sandboxed iframes); any failure shows the popup.
 */
export function getPromoSuppression(
  { id, isGlobal }: Campaign,
  ignoreDismissal: boolean,
): PromoSuppressionReason | null {
  try {
    if (sessionStorage.getItem(PROMO_DISABLED_SESSION_KEY) === "true") return "session_shown";
    if (sessionStorage.getItem(`${PROMO_SHOWN_SESSION_KEY}:${id}`) === "true") return "session_shown";
    if (isGlobal && sessionStorage.getItem(PROMO_SHOWN_SESSION_KEY) === "true") return "session_shown";
    if (ignoreDismissal) return null;
    if (isDismissed(`${PROMO_DISMISSED_KEY}:${id}`)) return "recently_dismissed";
    if (isGlobal && isDismissed(PROMO_DISMISSED_KEY)) return "recently_dismissed";

    return null;
  } catch {
    return null;
  }
}

/**
 * Takes a thunk rather than (storage, key, value):
 * In a sandboxed iframe, merely touching `sessionStorage` throws,
 * and an argument would be evaluated outside the try.
 */
function safeWrite(write: () => void) {
  try {
    write();
  } catch {
    // Storage blocked — suppression just won't stick.
  }
}

export function markPromoShown(id: string) {
  safeWrite(() => sessionStorage.setItem(`${PROMO_SHOWN_SESSION_KEY}:${id}`, "true"));
}

/** Starts the campaign's 30-day suppression window. Paired with the read in `getPromoSuppression`. */
export function markPromoDismissed(id: string) {
  safeWrite(() => localStorage.setItem(`${PROMO_DISMISSED_KEY}:${id}`, Date.now().toString()));
}

/**
 * Whether this session still owes a "Popup Suppressed" event, marking it reported if so.
 * Blocked storage reports every time rather than not at all — an over-count is recoverable
 * from the session id downstream, a silent gap is not.
 */
export function claimSuppressionReport(): boolean {
  try {
    if (sessionStorage.getItem(PROMO_SUPPRESSION_REPORTED_SESSION_KEY) === "true") return false;
  } catch {
    return true;
  }

  safeWrite(() => sessionStorage.setItem(PROMO_SUPPRESSION_REPORTED_SESSION_KEY, "true"));
  return true;
}
